"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ISDESubsidy2023 = exports.Tariff = void 0;
const glass_1 = require("../glass");
const solution_1 = require("../solution");
const sum_1 = require("../utils/sum");
const calculator_1 = require("./calculator");
const types_1 = require("./types");
const utils_1 = require("./utils");
var Tariff;
(function (Tariff) {
    /** Default: refers to the full tariffs used when user is installing 2 or more installations */
    Tariff["Multiple2022"] = "multiple2022";
    Tariff["Multiple2023"] = "multiple2023";
    /** refers to the halved tariffs used when user is installing 1 installable that was installed in 2022 after 2 april. */
    Tariff["Single2022"] = "single";
    /** refers to the halved tariffs used when user is installing 1 installable that was installed in 2023. Has slightly lower subsidy for glass and window framing */
    Tariff["Single2023"] = "single2023";
})(Tariff = exports.Tariff || (exports.Tariff = {}));
/**
 * According to RVO, Triple glazing must have U ≤ 0.7.
 */
function isTripleGlazing(product) {
    return product?.glassType === glass_1.GlassType.Triple && !!product?.u && product.u <= 0.7;
}
const subsidiesSingle2022 = {
    [solution_1.Solution.AtticFloorInsulation]: new types_1.SubsidyDetails(4, { min: 20, max: 130 }),
    [solution_1.Solution.CentralHeatingBoiler]: null,
    [solution_1.Solution.ChargingBox]: null,
    [solution_1.Solution.CrawlspaceInsulation]: new types_1.SubsidyDetails(3, { min: 20, max: 130 }),
    [solution_1.Solution.ElectricHeatPump]: new types_1.SubsidyDetails(3100, { min: 1, max: 3 }),
    [solution_1.Solution.AirToAirHeatPump]: null,
    [solution_1.Solution.FloorInsulation]: new types_1.SubsidyDetails(5.5, { min: 20, max: 130 }),
    [solution_1.Solution.HybridHeatPump]: new types_1.SubsidyDetails(2400, { min: 1, max: 3 }),
    [solution_1.Solution.InnerRoofInsulation]: new types_1.SubsidyDetails(15, { min: 20, max: 200 }),
    [solution_1.Solution.Miscellaneous]: null,
    [solution_1.Solution.PvSystem]: null,
    [solution_1.Solution.Sedum]: null,
    [solution_1.Solution.WallInsulation]: new types_1.SubsidyDetails(4, { min: 10, max: 170 }),
    [solution_1.Solution.WindowFrames]: new types_1.SubsidyDetails(11.5, { min: 8, max: 45 }),
    [solution_1.Solution.WindowGlazing]: new types_1.SubsidyDetails(
    // For HR++ glass the subsidy is €26.5 per m2. For Triple glass the subsidy is €75 per m2.
    product => (isTripleGlazing(product) ? 75 : 26.5), { min: 8, max: 45 }),
    [solution_1.Solution.UnderfloorHeating]: null,
    [solution_1.Solution.HomeBattery]: null,
};
const subsidiesSingle2023 = {
    ...subsidiesSingle2022,
    [solution_1.Solution.WindowFrames]: new types_1.SubsidyDetails(10, { min: 8, max: 45 }),
    [solution_1.Solution.WindowGlazing]: new types_1.SubsidyDetails(
    // For HR++ glass the subsidy is €23 per m2. For Triple glass the subsidy is €65.5 per m2.
    product => (isTripleGlazing(product) ? 65.5 : 23), { min: 8, max: 45 }),
};
function double(subsidies, except = []) {
    return Object.fromEntries(Object.entries(subsidies).map(([solution, details]) => {
        if (!details)
            return [solution, null];
        if (except.includes(solution))
            return [solution, details];
        const oldSubsidyPerAmount = details.subsidyPerAmount;
        const newSubsidyPerAmount = typeof oldSubsidyPerAmount === 'number'
            ? oldSubsidyPerAmount * 2
            : (product) => oldSubsidyPerAmount(product) * 2;
        return [solution, details ? new types_1.SubsidyDetails(newSubsidyPerAmount, details.amount) : null];
    }));
}
const keepSingle = [solution_1.Solution.ElectricHeatPump, solution_1.Solution.HybridHeatPump];
const subsidiesMultiple2022 = double(subsidiesSingle2022, keepSingle);
const subsidiesMultiple2023 = double(subsidiesSingle2023, keepSingle);
const subsidies = {
    [Tariff.Multiple2022]: subsidiesMultiple2022,
    [Tariff.Multiple2023]: subsidiesMultiple2023,
    [Tariff.Single2022]: subsidiesSingle2022,
    [Tariff.Single2023]: subsidiesSingle2023,
};
class ISDESubsidy2023 extends calculator_1.ISDESubsidyCalculator {
    constructor() {
        super(...arguments);
        this.year = 2023;
        this.eligibleWithin = {
            start: new Date('2022-01-01'),
        };
        this.bySolution = subsidies[Tariff.Multiple2023];
    }
    /**
     * In 2023, subsidy can be requested for one or more solutions,
     * though requesting subsidy for 1 solution gives about half as much subsidy per unit than when requesting subsidy for 2 or more solutions.
     */
    isSubsidizableSolutions(...solutions) {
        const subsidizable = solutions.filter(solution => this.getDetailsBySolution(solution) !== null);
        return subsidizable.length >= 1;
    }
    forInstallables(installables) {
        // Subsidy on a set of installables in 2023 can be requested for installations from 1 jan 2022 onwards. We simply ignore any older installations.
        // - "Vanaf 2023 heeft u als woningeigenaar maar liefst 24 maanden de tijd om uw subsidieaanvraag te doen nadat u uw maatregel heeft laten uitvoeren. Deze termijn geldt alleen voor maatregelen die u vanaf 1 januari 2022 heeft genomen."
        installables = (0, utils_1.filterInstalledWithin)(installables, this.eligibleWithin);
        // combine installables with the same solution
        installables = (0, utils_1.mergeSameSolutionInstallables)(installables);
        const solutions = installables.map(i => i.solution);
        // - Bij de isolatiemaatregel type vloer- of bodemisolatie kunt u ervoor kiezen om de vloer of de bodem te isoleren.
        // i.e., the customer may pick which one they will get subsidised. Obviously, we will pick the one which will net the most subsidy.
        if (solutions.includes(solution_1.Solution.FloorInsulation) &&
            solutions.includes(solution_1.Solution.CrawlspaceInsulation)) {
            const [floorInstallable, removeFloor] = (0, utils_1.findBySolution)(installables, solution_1.Solution.FloorInsulation); // eslint-disable-line prettier/prettier
            const [crawlspaceInstallable, removeCrawlspace] = (0, utils_1.findBySolution)(installables, solution_1.Solution.CrawlspaceInsulation); // eslint-disable-line prettier/prettier
            const floorSubsidy = this.forInstallable(floorInstallable);
            const crawlspaceSubsidy = this.forInstallable(crawlspaceInstallable);
            if (floorSubsidy > crawlspaceSubsidy) {
                removeCrawlspace();
            }
            else {
                removeFloor();
            }
        }
        // - Dakisolatie en zolder- of vlieringsvloerisolatie zijn niet te combineren als twee maatregelen.
        // i.e., the customer may pick which one they will get subsidised. Obviously, we will pick the one which will net the most subsidy.
        if (solutions.includes(solution_1.Solution.InnerRoofInsulation) &&
            solutions.includes(solution_1.Solution.AtticFloorInsulation)) {
            const [roofInstallable, removeRoof] = (0, utils_1.findBySolution)(installables, solution_1.Solution.InnerRoofInsulation); // eslint-disable-line prettier/prettier
            const [atticFloorInstallable, removeAtticFloor] = (0, utils_1.findBySolution)(installables, solution_1.Solution.AtticFloorInsulation); // eslint-disable-line prettier/prettier
            const roofSubsidy = this.forInstallable(roofInstallable);
            const atticFloorSubsidy = this.forInstallable(atticFloorInstallable);
            if (roofSubsidy > atticFloorSubsidy) {
                removeAtticFloor();
            }
            else {
                removeRoof();
            }
        }
        const subsidisableInstallables = installables.filter(installable => this.getDetailsBySolution(installable.solution) !== null);
        // if there's only one subsidisable quote being installed, then ISDE has some extra requirements, including using lower tariffs.
        if (subsidisableInstallables.length === 1) {
            const installable = subsidisableInstallables[0];
            // Subsidy for single solutions can only be requested if the installation was installed on 2 april 2022 or later
            if (installable.installationDate && installable.installationDate < new Date('2022-04-02')) {
                return 0;
            }
            // Subsidy for single solutions that were installed in 2022 is slightly higher for windows and window framing
            if (installable.installationDate && installable.installationDate < new Date('2023-01-01')) {
                return this.calculateSubsidyWithTariff(installable, Tariff.Single2022);
            }
            // if there's no installation date, we assume it'll be installed in 2023.
            return this.calculateSubsidyWithTariff(installable, Tariff.Single2023);
        }
        return (0, sum_1.sum)(installables.map(installable => {
            if (installable.installationDate && installable.installationDate < new Date('2023-01-01')) {
                return this.calculateSubsidyWithTariff(installable, Tariff.Multiple2022);
            }
            return this.calculateSubsidyWithTariff(installable, Tariff.Multiple2023);
        }));
    }
    calculateSubsidyWithTariff(installable, tariff) {
        this.bySolution = subsidies[tariff];
        const subsidy = this.forInstallable(installable);
        this.bySolution = subsidies[Tariff.Multiple2023];
        return subsidy;
    }
}
exports.ISDESubsidy2023 = ISDESubsidy2023;
